import { RECOGNITION_AWARD_TYPES, RECOGNITION_APPROVAL_TYPES } from '@/pages/recognition/constants';

// grant_expiry_interval round-trips as an ISO 8601 duration in days (e.g. "P360D"),
// matching how the create form serialises months (months * 30 days).
function expiryToMonths(iso) {
  if (!iso) {
    return '';
  }
  const days = String(iso).match(/^P(\d+)D$/);
  if (days) {
    return String(Math.round(parseInt(days[1], 10) / 30));
  }
  const months = String(iso).match(/^P(\d+)M$/);
  if (months) {
    return months[1];
  }
  return '';
}

function monthsToExpiry(months) {
  const n = parseInt(String(months ?? '').trim(), 10);
  if (Number.isFinite(n) && n > 0) {
    return `P${n * 30}D`;
  }
  return undefined;
}

function companyValuesToNames(values) {
  return (values || []).map(v => (typeof v === 'string' ? v : v && v.name)).filter(Boolean);
}

/**
 * Map a backend program row to a flat editor-row model the tabular editor binds to.
 */
export function programToRow(program) {
  const props = program.properties || {};
  const approverGroup = (props.approvers || [])[0] || {};
  const companyValues = companyValuesToNames(props.company_values);
  // Monetary = there is a reward amount. A grant expiry is a redemption attribute that
  // can exist on non-monetary awards, so it must not flip this on.
  const isMonetary = props.award_value_minor != null || props.max_award_value_minor != null;
  return {
    recognition_award_program_id: program.recognition_award_program_id || null,
    program_type: program.program_type,
    program_name: program.program_name || '',
    enabled_divisions: (program.enabled_divisions || []).map(String),
    company_values: companyValues,
    company_values_enabled: companyValues.length > 0,
    short_description: props.short_description || '',
    milestone_event_type: (props.milestone && props.milestone.event_type) || null,
    recipient_message: (props.milestone && props.milestone.recipient_message) || '',
    // Preserve the full milestone object (anniversary_year etc.) — merged back on save.
    milestone: props.milestone || null,

    // Monetary
    monetary_enabled: isMonetary,
    amount_mode: props.max_award_value_minor != null ? 'free' : 'fixed',
    award_value_minor: props.award_value_minor ?? null,
    default_award_value_minor: props.default_award_value_minor ?? null,
    max_award_value_minor: props.max_award_value_minor ?? null,
    budget_minor: program.budget_minor ?? null,
    // Period-aware spend from the enriched program payload (0 for new copies).
    budget_spent_minor: program.budget_spent_minor ?? 0,
    budget_cadence: props.budget_period || null,
    categories: (props.categories || []).slice(),
    spend_categories_enabled: (props.categories || []).length > 0,
    category_required: props.category_required ?? false,

    // Redemption
    show_badge: program.show_badge ?? true,
    redemption_methods: (props.valid_grant_types || []).slice(),
    expiration_months: expiryToMonths(program.grant_expiry_interval),

    // Nominations & Approvals
    nominators: (props.nominators || []).map(String),
    approvers: (approverGroup.approvers || []).map(String),
    approval_type: program.approval_type || 'NONE',

    // Appearance (preview only)
    banner_s3_key: program.banner_s3_key || '',
    badge_s3_key: program.badge_s3_key || '',
    banner_presigned_url: program.banner_presigned_url || '',
    badge_presigned_url: program.badge_presigned_url || '',

    is_active: program.is_active ?? true,
  };
}

function buildProperties(row) {
  const isMilestone = row.program_type === RECOGNITION_AWARD_TYPES.MILESTONE;
  const props = {
    // Backend requires at least one grant type.
    valid_grant_types: (row.redemption_methods || []).length ? row.redemption_methods : ['VOUCHER'],
    short_description: row.short_description ? String(row.short_description) : null,
  };

  if (row.company_values_enabled && (row.company_values || []).length) {
    props.company_values = row.company_values.map(name => ({ name: String(name) }));
  } else {
    // Explicit clear: properties are merged server-side, so omitting the key on a
    // bulk-update would keep previously stored values.
    props.company_values = [];
  }

  if (!row.monetary_enabled) {
    // Clear monetary fields so a disabled award isn't treated as monetary.
    props.award_value_minor = null;
    props.default_award_value_minor = null;
    props.max_award_value_minor = null;
    props.budget_period = null;
  } else if (isMilestone) {
    // Milestone: a single fixed amount, no fixed/adjustable mode. The canonical
    // milestone forms mirror it into default_award_value_minor, so do the same.
    props.award_value_minor = row.award_value_minor ?? null;
    props.default_award_value_minor = row.award_value_minor ?? null;
  } else if (row.amount_mode === 'free') {
    props.award_value_minor = null;
    props.default_award_value_minor = null;
    props.max_award_value_minor = row.max_award_value_minor ?? null;
  } else {
    props.award_value_minor = row.award_value_minor ?? null;
    props.default_award_value_minor = row.default_award_value_minor ?? null;
    props.max_award_value_minor = null;
  }

  if (row.monetary_enabled && row.budget_cadence && row.budget_minor != null) {
    props.budget_period = String(row.budget_cadence);
  } else {
    // Explicit clear: without a budget a bulk-update would otherwise keep the
    // previously stored period (server-side properties merge).
    props.budget_period = null;
  }

  if (row.monetary_enabled && row.spend_categories_enabled && (row.categories || []).length) {
    props.categories = row.categories.map(c => String(c));
    props.category_required = Boolean(row.category_required);
  } else {
    // Explicit clear so disabling spend categories on a bulk-update removes the
    // stored ones (server-side properties merge would otherwise keep them).
    props.categories = [];
    props.category_required = false;
  }

  if (row.program_type === RECOGNITION_AWARD_TYPES.PROGRAM) {
    props.nominators = (row.nominators || []).length ? row.nominators.map(String) : null;
    props.approvers = (row.approvers || []).length ? [{ name: 'Default', approvers: row.approvers.map(String) }] : null;
  } else if (row.program_type === RECOGNITION_AWARD_TYPES.OPEN) {
    props.approvers = (row.approvers || []).length ? [{ name: 'Default', approvers: row.approvers.map(String) }] : null;
  }

  if (isMilestone && (row.milestone || row.milestone_event_type)) {
    props.milestone = {
      ...(row.milestone || {}),
      ...(row.milestone_event_type ? { event_type: row.milestone_event_type } : {}),
      recipient_message: row.recipient_message || (row.milestone && row.milestone.recipient_message) || null,
    };
  }

  return props;
}

// Derive approval_type from the row the same way the canonical create forms do, so
// editing approvers in the bulk editor can't leave a stale approval_type. Program awards
// always require approval; Open awards require it only when approvers are set; Milestone
// (and anything else) never do.
function deriveApprovalType(row) {
  if (row.program_type === RECOGNITION_AWARD_TYPES.PROGRAM) {
    return RECOGNITION_APPROVAL_TYPES.ALL;
  }
  if (row.program_type === RECOGNITION_AWARD_TYPES.OPEN) {
    return (row.approvers || []).length ? RECOGNITION_APPROVAL_TYPES.AMOUNT : RECOGNITION_APPROVAL_TYPES.NONE;
  }
  return RECOGNITION_APPROVAL_TYPES.NONE;
}

/**
 * Build the create payload for one new copy (POST /awards/duplicate members[]).
 */
export function rowToCreateMember(row) {
  const member = {
    program_type: row.program_type,
    program_name: row.program_name,
    enabled_divisions: row.enabled_divisions || [],
    banner_s3_key: row.banner_s3_key || null,
    badge_s3_key: row.badge_s3_key || null,
    budget_minor: row.monetary_enabled ? row.budget_minor : null,
    show_badge: row.show_badge,
    is_active: row.is_active,
    approval_type: deriveApprovalType(row),
    properties: buildProperties(row),
  };
  // Always send it (null when non-monetary or unset) so a bulk-update clears a
  // previously stored expiry instead of keeping it.
  member.grant_expiry_interval = row.monetary_enabled ? monthsToExpiry(row.expiration_months) ?? null : null;
  return member;
}

/**
 * Build the update payload for one existing member (PATCH /award-groups/:id/members).
 */
export function rowToUpdateMember(row) {
  return { recognition_award_program_id: row.recognition_award_program_id, ...rowToCreateMember(row) };
}
